import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button, Card, Checkbox, DatePicker, Empty, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, Upload, notification,
} from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CategoriesProxy } from '../services/catalogServiceAPI/categoriesProxy';
import { CategoryIcon } from './CategoryIcon';
import { TYPE_OPTIONS } from './CategoryForm';

const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

const STATUS_COLORS = {
    scheduled: 'blue',
    active: 'green',
    done: 'default',
    cancelled: 'default',
    failed: 'red',
};

const FIELDS = [
    { key: 'type', label: 'Tab' },
    { key: 'parentId', label: 'Parent category' },
    { key: 'iconUrl', label: 'Icon' },
    { key: 'title', label: 'Title' },
    { key: 'priority', label: 'Priority' },
];

const typeLabel = (type) => TYPE_OPTIONS.find((o) => o.value === (type ?? ''))?.label;

function ChangeValue({ field, value, categories }) {
    if (field === 'iconUrl') {
        return <CategoryIcon src={value ?? null} />;
    }
    if (field === 'type') return typeLabel(value);
    if (field === 'parentId') return value ? categories.find((c) => c.id === value)?.title ?? value : 'None (top level)';
    return value === null || value === undefined || value === '' ? <Text type="secondary">none</Text> : String(value);
}

function ChangeList({ schedule, categories }) {
    return (
        <Space direction="vertical" size={2}>
            {Object.entries(schedule.changes).map(([field, value]) => (
                <span key={field}>
                    <Text type="secondary">{FIELDS.find((f) => f.key === field)?.label ?? field}: </Text>
                    {schedule.previousValues && (
                        <>
                            <ChangeValue field={field} value={schedule.previousValues[field]} categories={categories} />
                            {' → '}
                        </>
                    )}
                    <ChangeValue field={field} value={value} categories={categories} />
                </span>
            ))}
        </Space>
    );
}

/** The add / edit dialog. A live schedule can only move its end; everything else is locked. */
function ScheduleDialog({ open, schedule, category, categories, onClose, onSaved }) {
    const proxy = useMemo(() => new CategoriesProxy(), []);
    const [form] = Form.useForm();
    const [fields, setFields] = useState([]);
    const [iconUrl, setIconUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const live = schedule?.status === 'active';

    useEffect(() => {
        if (!open) return;
        const changes = schedule?.changes ?? {};
        setFields(Object.keys(changes));
        setIconUrl(changes.iconUrl ?? null);
        form.setFieldsValue({
            name: schedule?.name ?? '',
            startsAt: schedule ? dayjs(schedule.startsAt) : null,
            endsAt: schedule?.endsAt ? dayjs(schedule.endsAt) : null,
            type: changes.type ?? 'items',
            parentId: changes.parentId ?? undefined,
            title: changes.title ?? category.title,
            priority: changes.priority ?? category.priority ?? undefined,
        });
    }, [open, schedule, category, form]);

    const parentOptions = categories
        .filter((c) => c.id !== category.id)
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((c) => ({ value: c.id, label: c.title }));

    const uploadIcon = async (file) => {
        setUploading(true);
        try {
            setIconUrl((await proxy.uploadIcon(file)).url);
        } catch (e) {
            notification.error({ message: 'Icon rejected', description: e.message });
        } finally {
            setUploading(false);
        }
        return false;
    };

    const save = async () => {
        const values = await form.validateFields();
        const changes = {};
        fields.forEach((field) => {
            if (field === 'iconUrl') changes.iconUrl = iconUrl;
            else if (field === 'priority') changes.priority = values.priority === '' || values.priority === undefined ? null : Number(values.priority);
            else if (field === 'parentId') changes.parentId = values.parentId ?? null;
            else changes[field] = values[field];
        });
        setSaving(true);
        try {
            await proxy.saveSchedule(schedule?.id, {
                categoryId: category.id,
                name: values.name,
                startsAt: values.startsAt.toISOString(),
                endsAt: values.endsAt ? values.endsAt.toISOString() : null,
                changes,
            });
            notification.success({ message: 'Schedule saved' });
            onSaved();
        } catch (e) {
            notification.error({ message: 'Could not save the schedule', description: e.message });
        } finally {
            setSaving(false);
        }
    };

    const has = (field) => fields.includes(field);

    return (
        <Modal
            open={open}
            title={schedule ? `Edit "${schedule.name}"` : 'Schedule a change'}
            onCancel={onClose}
            onOk={save}
            okText="Save"
            confirmLoading={saving}
            okButtonProps={{ disabled: uploading || (!live && fields.length === 0) }}
            width={560}
            forceRender
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name the schedule, e.g. "Halloween 2026"' }]}>
                    <Input maxLength={120} disabled={live} placeholder="e.g. Halloween 2026" />
                </Form.Item>
                <Space size="large" wrap>
                    <Form.Item label="Start (your local time)" name="startsAt" rules={[{ required: true, message: 'Pick when it starts' }]}>
                        <DatePicker showTime format={DATE_FORMAT} disabled={live} />
                    </Form.Item>
                    <Form.Item label="End (your local time)" name="endsAt">
                        <DatePicker showTime format={DATE_FORMAT} />
                    </Form.Item>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginTop: -12, marginBottom: 16 }}>
                    At the start the changes below go live (within a minute). At the end the changed fields go back to what they
                    were just before the start; leave the end empty to keep the change.
                </Text>
                <Form.Item label="Change">
                    <Checkbox.Group
                        disabled={live}
                        value={fields}
                        onChange={setFields}
                        options={FIELDS.map((f) => ({ value: f.key, label: f.label }))}
                    />
                </Form.Item>
                {has('type') && (
                    <Form.Item label="Tab" name="type" extra="Top-level categories only: the catalog tab it shows in.">
                        <Select options={TYPE_OPTIONS} disabled={live} />
                    </Form.Item>
                )}
                {has('parentId') && (
                    <Form.Item label="Parent category" name="parentId">
                        <Select options={parentOptions} allowClear showSearch optionFilterProp="label" placeholder="None (top level)" disabled={live} />
                    </Form.Item>
                )}
                {has('iconUrl') && (
                    <Form.Item label="Icon">
                        <Space align="center">
                            <div style={{ width: 56, height: 56, display: 'grid', placeItems: 'center', background: '#fcfdfd', border: '1px solid #e8e8e8', borderRadius: 12 }}>
                                {iconUrl
                                    ? <img src={iconUrl} alt="" style={{ width: 36, height: 36 }} />
                                    : <Text style={{ fontSize: 11, color: '#999' }}>none</Text>}
                            </div>
                            <Upload accept=".svg,image/svg+xml" showUploadList={false} beforeUpload={uploadIcon} disabled={live}>
                                <Button icon={<UploadOutlined />} loading={uploading} disabled={live}>Upload SVG</Button>
                            </Upload>
                        </Space>
                    </Form.Item>
                )}
                {has('title') && (
                    <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Give the category a title' }]}>
                        <Input maxLength={120} disabled={live} />
                    </Form.Item>
                )}
                {has('priority') && (
                    <Form.Item label="Priority" name="priority" extra="Lower numbers come first.">
                        <Input type="number" min={0} step={1} disabled={live} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
}

/** Timed changes to one category (catalogue-service CategoryScheduleService), shown under its edit form. */
export function CategorySchedules({ category, categories }) {
    const proxy = useMemo(() => new CategoriesProxy(), []);
    const [schedules, setSchedules] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        proxy.listSchedules(category.id)
            .then(setSchedules)
            .catch((e) => notification.error({ message: 'Failed to load schedules', description: e.message }));
    }, [proxy, category.id]);

    useEffect(load, [load]);

    const cancel = async (schedule) => {
        try {
            await proxy.cancelSchedule(schedule.id);
            notification.success({ message: schedule.status === 'active' ? 'Change taken back' : 'Schedule cancelled' });
            load();
        } catch (e) {
            notification.error({ message: 'Could not cancel', description: e.message });
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            render: (name, s) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Tag color={STATUS_COLORS[s.status]}>{s.status}</Tag>
                    {s.error && <Text type="danger" style={{ fontSize: 12 }}>{s.error}</Text>}
                </Space>
            ),
        },
        {
            title: 'When',
            render: (_, s) => (
                <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
                    <span>{dayjs(s.startsAt).format(DATE_FORMAT)}</span>
                    <Text type="secondary">{s.endsAt ? `until ${dayjs(s.endsAt).format(DATE_FORMAT)}` : 'no end'}</Text>
                </Space>
            ),
        },
        { title: 'Changes', render: (_, s) => <ChangeList schedule={s} categories={categories} /> },
        {
            title: '',
            align: 'right',
            render: (_, s) => (s.status === 'scheduled' || s.status === 'active') && (
                <Space>
                    <Button size="small" onClick={() => setEditing(s)}>Edit</Button>
                    <Popconfirm
                        title={s.status === 'active' ? 'Take the change back now?' : 'Cancel this schedule?'}
                        onConfirm={() => cancel(s)}
                    >
                        <Button size="small" danger>{s.status === 'active' ? 'End now' : 'Cancel'}</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Scheduled changes"
            extra={<Button icon={<PlusOutlined />} onClick={() => setEditing('new')}>Schedule a change</Button>}
            style={{ marginTop: 8 }}
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Open a category for a season, switch its icon for a holiday, move it to another tab: the change goes live at the
                start and is taken back at the end, with no deploy.
            </Text>
            {schedules && schedules.length === 0
                ? <Empty description="No scheduled changes" />
                : <Table rowKey="id" size="small" loading={!schedules} dataSource={schedules ?? []} columns={columns} pagination={false} />}
            <ScheduleDialog
                open={editing !== null}
                schedule={editing === 'new' ? null : editing}
                category={category}
                categories={categories}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    load();
                }}
            />
        </Card>
    );
}
